import { ColorId } from '@verify/shared-utils';
import {
  InspectionState,
  DocumentStates,
  DocumentData,
  DocumentTextProcessing,
  PageRangeOption,
  DocumentTypes,
  ShowLibrary,
  PDFTRON_DEFAULT_TOOL,
  SortDifferencesOption,
} from 'types';

const initialDocumentState: DocumentData = {
  state: DocumentStates.INITIAL,
  pageRange: [],
  annotations: [],
  liveText: false,
  documentTextProcessing: DocumentTextProcessing.INITIAL,
  zoneSelectedTexts: [],
  pages: [],
  totalPageCount: 0,
  currentPage: 1,
  layers: undefined,
  separations: undefined,
  overprint: true,
};

const initialDifferenceStatusFilters = { smartDiscard: true, unassigned: false };

const showLibrary: ShowLibrary = {
  [DocumentTypes.source]: false,
  [DocumentTypes.target]: false,
};

const initialInspectionState: InspectionState = {
  source: initialDocumentState,
  target: initialDocumentState,
  report: initialDocumentState,
  selectedTool: PDFTRON_DEFAULT_TOOL,
  differenceViewOptions: {
    sortBy: SortDifferencesOption.readingOrder,
    differenceTypeFilters: {},
    differenceStatusFilters: initialDifferenceStatusFilters,
  },
  differencesLoaded: false,
  focusedDifferenceId: '-1',
  previousDifferenceId: '-1',
  syncScrolling: false,
  noResultAnnotationId: '',
  invalidScaledGraphicZoneRatio: false,
  unconfirmTextZoneError: false,
  unconfirmedGraphicZoneError: false,
  unconfirmedMarqueeZoneError: false,
  changeToolError: '',
  pendingCropZone: { documentType: null, pendingCropId: '' },
  activePolygonCreation: {
    isActive: false,
    documentType: null,
    pageNumber: null,
    currentPage: null,
  },
  pendingShapeRequest: null,
  manualSelectedZoneId: 0,
  manualSelectedAnnotationId: '',
  loadingInspection: false,
  name: '',
  inspectionId: '',
  inspectionStatus: '',
  lastJobId: null,
  lastJobInput: null,
  lastJobCtfVersion: null,
  lastJobCreatedAt: null,
  ctfJobs: [],
  textMatchJobId: null,
  textMatchCdnPathPrefix: null,
  textMatchMetadata: null,
  selectingZone: false,
  openPanel: false,
  pageRangeOption: PageRangeOption.ALL,
  showLibrary,
  internalAnnotationsVisibility: true,
  internalAnnotationsVisibilityDefault: true,
  tableEnhancementEnabledState: false,
  nextTextZoneId: 1,
  nextGraphicZoneId: 1,
  nextCropZoneId: 1,
  nextMarqueeZoneId: 1,
  nextCrossoutZoneId: 1,
  nextColorPointId: 1,
  nextManualAlignZoneId: 1,
  settings: {
    tableEnhancement: false,
    dictionaryName: undefined,
    spelling: false,
    fullPageGraphics: false,
    enableLENFileMode: false,
    barcode: false,
    singleFile: false,
    fontSizeCheckValue: undefined,
    highlightColor: ColorId.MULTICOLOR,
    isSharing: false, // tracks if user has enabled sharing (isSharing === true) even if no collaborators are added yet
    resyncCount: 1,
    hasShownResyncToolToast: false,
    cumulativeNetResyncedDifferences: 0,
    originalOrgId: '', // tracks if the inspection is cloned, contains the org id of the original inspection
    groupId: undefined,
  },
  autoMatchGraphic: {
    autoMatchFail: 0,
    autoMatchPass: 0,
  },
  customDictionaryIDs: [],
  updatedAt: '',
  differencePanelOpen: true,
  changeGridDirection: 'ltr',
  changeGridWidth: null,
  shiftedGraphicRefId: '',
  persistedDifferenceComments: {},
  isFilesLoaded: false,
  searchingForGraphicZone: false,
  reloadingDifferences: false,
  resyncStatus: {
    loading: false,
    processingResults: false,
    cancelled: false,
    undoingResync: false,
  },
  isBarcode: false,
  brailleLanguage: '',
  unmatchedPages: [],
  unmatchedCropPages: [],
  isResultsPanelHidden: false,
  flashMode: true,
  lastJobMatches: [],
  lastJobImages: undefined,
  lastJobUnmatchedCropIds: undefined,
  polygons: {
    [DocumentTypes.source]: { points: '', key: '', type: '', layerOrSeparationName: '' },
    [DocumentTypes.target]: { points: '', key: '', type: '', layerOrSeparationName: '' },
  },
  userId: undefined,
  manualAlignMode: {
    isActive: false,
    cropZoneId: null,
    cropZonePageNumber: null,
    originZoneDocumentType: null,
    masterAnchorPoint: null,
    targetAnchorPoint: null,
  },
};

export { initialDocumentState, initialInspectionState, initialDifferenceStatusFilters };
